import { useQuery } from "@tanstack/react-query";
import type { PreviewFile } from "./previewFile";

/** Text formats we render in the browser (the server only previews PDF and Office files) and can show as source. */
const TEXT_KINDS = { md: "markdown", markdown: "markdown", csv: "csv", html: "html", htm: "html" } as const;

export type TextKind = (typeof TEXT_KINDS)[keyof typeof TEXT_KINDS];

export function textKindOf(ext: string): TextKind | undefined {
  return TEXT_KINDS[ext as keyof typeof TEXT_KINDS];
}

/** A file's original content as text. Shared by the Preview and Source tabs. */
export function useDocumentText(file: PreviewFile) {
  return useQuery({
    queryKey: [...file.key, "text"],
    queryFn: async () => (await file.original()).blob.text(),
    // A version's file never changes.
    staleTime: Number.POSITIVE_INFINITY,
    retry: false,
  });
}

/** Reads a quoted field starting just after its opening quote; returns the value and the index after the closing quote. */
function readQuoted(text: string, start: number): [string, number] {
  let value = "";
  let i = start;
  while (i < text.length) {
    if (text[i] !== '"') {
      value += text[i];
      i++;
    } else if (text[i + 1] === '"') {
      value += '"';
      i += 2;
    } else {
      return [value, i + 1];
    }
  }
  return [value, i];
}

/** RFC 4180 CSV: quoted fields, doubled quotes, commas and newlines inside quotes. */
export function parseCsv(text: string): string[][] {
  const rows: string[][] = [];
  let row: string[] = [];
  let field = "";
  let i = 0;
  const endRow = () => {
    row.push(field);
    rows.push(row);
    row = [];
    field = "";
  };
  while (i < text.length) {
    const c = text[i];
    if (c === '"') {
      const [value, next] = readQuoted(text, i + 1);
      field += value;
      i = next;
      continue;
    }
    if (c === ",") {
      row.push(field);
      field = "";
    } else if (c === "\n" || c === "\r") {
      if (c === "\r" && text[i + 1] === "\n") i++;
      endRow();
    } else {
      field += c;
    }
    i++;
  }
  if (field || row.length) endRow();
  return rows;
}

/** A Markdown code fence longer than any backtick run in the text, so the text can't close it. */
export function fenced(text: string, language: string): string {
  const longest = Math.max(2, ...[...text.matchAll(/`+/g)].map((m) => m[0].length));
  const fence = "`".repeat(longest + 1);
  return `${fence}${language}\n${text}\n${fence}`;
}
